import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Grid,
  Avatar,
  Divider,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";
import React from "react";
import api from "../../axiosInstance";
import { toast } from "react-toastify";
import useUniversitiesList from "../../hooks/useUniversitiesList";
import CloseIcon from "@mui/icons-material/Close";
import UpdateIcon from "@mui/icons-material/Update";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const FIELD_META = [
  {
    name: "universityIds",
    label: "University",
    labelKey: "name",
    icon: SchoolIcon,
  },
  {
    name: "courseIds",
    label: "Course",
    labelKey: "courseName",
    icon: MenuBookIcon,
  },
  {
    name: "regulationYearIds",
    label: "Regulation Year",
    labelKey: "regulationName",
    icon: EventNoteIcon,
  },
  {
    name: "semesterIds",
    label: "Semester",
    labelKey: "name",
    icon: CalendarMonthIcon,
  },
];

function UpdateSemesterModal({ open, onClose, onSuccess }) {
  const { universityList, courses, semesters, regulations } =
    useUniversitiesList({});

  const formik = useFormik({
    initialValues: {
      universityIds: [],
      courseIds: [],
      regulationYearIds: [],
      semesterIds: [],
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      if (
        !values.universityIds.length &&
        !values.courseIds.length &&
        !values.regulationYearIds.length &&
        !values.semesterIds.length
      ) {
        toast.error("Please select at least one filter before updating.");
        return;
      }

      setSubmitting(true);
      try {
        const payload = {
          universityIds: values.universityIds.map(Number),
          courseIds: values.courseIds.map(Number),
          regulationYearIds: values.regulationYearIds.map(Number),
          semesterIds: values.semesterIds.map(Number),
        };

        await api.post("/api/admin/students/updateSemester", payload);

        toast.success("Semester updated successfully!", {
          position: "top-right",
          autoClose: 2000,
        });

        resetForm();
        onSuccess?.();
        onClose();
      } catch (error) {
        const errorMessage =
          error.response?.data?.errorMessage ||
          "Failed to update semester. Please try again.";
        toast.error(errorMessage, { autoClose: 3000 });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const listByField = {
    universityIds: universityList,
    courseIds: courses.filter(
      (course) =>
        !formik.values.universityIds.length ||
        formik.values.universityIds.includes(Number(course.universityId)),
    ),
    regulationYearIds: regulations.filter(
      (reg) =>
        !formik.values.courseIds.length ||
        formik.values.courseIds.includes(Number(reg.courseId)),
    ),
    semesterIds: semesters.filter(
      (sem) =>
        !formik.values.courseIds.length ||
        courses.some(
          (course) =>
            formik.values.courseIds.includes(Number(course.id)) &&
            course?.semesters?.includes(sem.id),
        ),
    ),
  };

  const handleFieldChange = (name) => (e) => {
    formik.setFieldValue(name, e.target.value);
    if (name === "universityIds") {
      formik.setFieldValue("courseIds", []);
      formik.setFieldValue("regulationYearIds", []);
      formik.setFieldValue("semesterIds", []);
    } else if (name === "courseIds") {
      formik.setFieldValue("regulationYearIds", []);
      formik.setFieldValue("semesterIds", []);
    }
  };

  const totalSelected = FIELD_META.reduce(
    (sum, field) => sum + formik.values[field.name].length,
    0,
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 2.5,
          background: "linear-gradient(135deg, #17479e 0%, #2f6fdb 100%)",
          color: "#fff",
        }}
      >
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff" }}>
          <UpdateIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Update Semester
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            Bulk-update students matching the selected filters
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box component="form" onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            {FIELD_META.map(({ name, label, labelKey, icon: Icon }) => {
              const list = listByField[name];
              return (
                <Grid item xs={12} sm={6} key={name}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 0.75,
                      color: "text.secondary",
                    }}
                  >
                    <Icon fontSize="small" />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {label}
                    </Typography>
                  </Box>
                  <FormControl fullWidth size="small">
                    <InputLabel>{`Select ${label}`}</InputLabel>
                    <Select
                      multiple
                      label={`Select ${label}`}
                      name={name}
                      value={formik.values[name]}
                      onChange={handleFieldChange(name)}
                      sx={{ borderRadius: 2, backgroundColor: "#fff" }}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const item = list.find(
                              (i) => Number(i.id) === Number(value),
                            );
                            return (
                              <Chip
                                key={value}
                                label={item ? item[labelKey] : value}
                                size="small"
                                color="primary"
                                variant="outlined"
                                onDelete={() => {
                                  formik.setFieldValue(
                                    name,
                                    formik.values[name].filter(
                                      (id) => Number(id) !== Number(value),
                                    ),
                                  );
                                }}
                                onMouseDown={(event) =>
                                  event.stopPropagation()
                                }
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {list.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item[labelKey]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              );
            })}
          </Grid>

          {totalSelected > 0 && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 2.5, color: "text.secondary" }}
            >
              <strong>{totalSelected}</strong> filter
              {totalSelected > 1 ? "s" : ""} selected
            </Typography>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
              background: "linear-gradient(135deg, #17479e 0%, #2f6fdb 100%)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, #123a80 0%, #255bb5 100%)",
              },
            }}
          >
            {formik.isSubmitting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />
                Updating...
              </>
            ) : (
              "Update"
            )}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default UpdateSemesterModal;
